import { useState, FC, MouseEvent } from 'react'
import { AfterPayIcon, SVGIcon } from 'shared/components/Icons'
import styled from 'styled-components'
import Modal from 'shared/components/Modal'
import { zIndex, fonts } from 'shared/lib'
import Image from 'shared/components/Image'
import { FCLink } from 'shared/components/Links'
import { ProductQA } from 'shared/dataAttributes'
import { mediaQueries } from 'shared/theme'
import { t } from 'localization'
import { Trans } from 'react-i18next'
import { useTranslation } from 'next-i18next'

const TP = 'productTemplate.components.AfterPayPromotional'

export enum AfterPayPromotionalType {
  Symbol,
  Text,
}

interface IAfterPayPromotional {
  type?: AfterPayPromotionalType
}

export const AfterPayPromotional: FC<IAfterPayPromotional> = ({
  type = AfterPayPromotionalType.Symbol,
}) => {
  const tv = useTranslation()
  const [isAfterPayModalOpen, setIsAfterPayModalOpen] = useState(false)
  const openAfterPayModal = () => setIsAfterPayModalOpen(true)
  const handleCloseAfterPayModal = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault()
    setIsAfterPayModalOpen(false)
  }
  const handleLearnMoreClick = (event: MouseEvent<HTMLSpanElement>) => {
    event.preventDefault()
    openAfterPayModal()
  }

  return (
    <>
      <AfterPayPromotionalWrapper>
        {type === AfterPayPromotionalType.Symbol ? (
          <AfterPayButton onClick={openAfterPayModal} data-testid="afterpay-promotion">
            <AfterPaySymbolText
              data-qa={ProductQA.AftepayPromotionLink}
              data-testid="afterpay-text"
            >
              {t(`${TP}.afterPayText1`, 'Or 4 interest-free payments by')}
            </AfterPaySymbolText>
            <AfterPayIcon data-testid="afterpay-icon" />
          </AfterPayButton>
        ) : (
          <AfterPayText>
            <Trans
              i18nKey={`${TP}.afterPayText2`}
              t={tv.t}
              defaults="4 Interest-free payments <0>Learn more</0><1> (US only)</1>"
              components={[
                <StyledFCLink
                  href="javascript:void(0)"
                  onClick={handleLearnMoreClick}
                  data-testid="afterpay-promotion"
                />,
                <span />,
              ]}
            />
          </AfterPayText>
        )}
      </AfterPayPromotionalWrapper>
      <Modal
        data-testid="afterpay-modal"
        widthOverride="auto"
        isOpen={isAfterPayModalOpen}
        onClose={handleCloseAfterPayModal}
      >
        <ContentWrapper>
          <AfterPayPromotionalContent data-testid="afterpay-modal-content">
            <XIconWrapper
              data-qa={ProductQA.AftepayPromotionCloseIcon}
              data-testid="afterpay-modal-x"
              onClick={handleCloseAfterPayModal}
            >
              <XIcon />
            </XIconWrapper>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.afterpay.com/purchase-payment-agreement"
            >
              <Img
                className="mobile"
                src="https://static-us.afterpay.com/assets/modal/us-mobile@2x.png"
                alt="afterpay promotional"
              />
              <Img
                className="desktop"
                src="https://static-us.afterpay.com/assets/modal/us@2x.png"
                alt="afterpay promotional"
              />
            </a>
          </AfterPayPromotionalContent>
        </ContentWrapper>
      </Modal>
    </>
  )
}

const AfterPayPromotionalWrapper = styled.div`
  display: flex;
  align-content: center;
  ${fonts.BODY_TEXT}
`

const AfterPaySymbolText = styled.span`
  line-height: 18px;
  text-decoration: underline;
  cursor: pointer;
  margin-right: 7px;
`

const AfterPayText = styled.span`
  font-size: 12px;
  line-height: 14px;
`

const XIconWrapper = styled.div`
  z-index: ${zIndex.justOverNav};
  position: absolute;
  cursor: pointer;
  padding: 20px;
  right: 0;
`

const XIcon = styled(SVGIcon).attrs({
  size: 'small',
  name: 'closeX',
})``

const ContentWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  overflow-y: auto;
`

const AfterPayPromotionalContent = styled.div`
  position: relative;
  padding: 0;
  width: 100%;
  height: 85vh;
`

const Img = styled(Image)`
  width: 100%;
  &.mobile {
    ${mediaQueries.tabletOrLarger} {
      display: none;
    }
  }
  &.desktop {
    ${mediaQueries.mobile} {
      display: none;
    }
  }
`

const AfterPayButton = styled.button`
  display: flex;
  align-content: center;
`

const StyledFCLink = styled(FCLink)`
  font-weight: normal;
  font-size: 12px;
  line-height: 14px;
`
